import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { APP_PALETTE } from "../../theme/palette";
import ConfirmDialog from "../common/ConfirmDialog";
import { formatCurrencyBs } from "../../utils/formatters";

const OPEN_ORDER_STATUSES = new Set(["pending_payment", "payment_in_review", "payment_rejected"]);

export default function PaymentSuggestionPanel({
  suggestions,
  orders,
  processingAction,
  showReassign,
  setShowReassign,
  reassignOrder,
  setReassignOrder,
  onSuggestionAction,
  onOpenOrderCompletion,
}) {
  const [advancedMenu, setAdvancedMenu] = useState({ anchorEl: null, suggestionId: null });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPayload, setConfirmPayload] = useState(null);
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);
  const [rejectPayload, setRejectPayload] = useState(null);

  const openAdvancedMenu = (event, suggestionId) => {
    setAdvancedMenu({ anchorEl: event.currentTarget, suggestionId });
  };

  const closeAdvancedMenu = () => {
    setAdvancedMenu({ anchorEl: null, suggestionId: null });
  };

  return (
    <>
      <Paper sx={{ p: 2.5, mb: 2.5, borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.08)" }}>
      <Typography variant="h6" fontWeight={700} color={APP_PALETTE.text.primary} mb={1.5}>
        Comprobantes por revisar
      </Typography>
      {suggestions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No hay comprobantes pendientes</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {suggestions.slice(0, 8).map((s) => {
            const reassignCandidates = orders.filter((o) => {
              if (!OPEN_ORDER_STATUSES.has(o.status)) return false;
              if (!s.matched_client_name) return true;
              return (o.client?.full_name || "").toLowerCase().includes(s.matched_client_name.toLowerCase());
            });
            const amountLabel = s.extracted_amount != null
              ? `Monto: ${formatCurrencyBs(s.extracted_amount)}`
              : "Monto: no detectado";

            return (
              <Paper key={s.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={700}>
                      Comprobante #{s.id} • {s.matched_client_name ? `Cliente: ${s.matched_client_name}` : "Cliente nuevo o no registrado"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                      Pedido sugerido: #{s.matched_order_id || "-"} • Referencia: {s.extracted_reference || "No disponible"}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={(e) => openAdvancedMenu(e, s.id)}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1, mb: 1 }}>
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={amountLabel}
                    sx={{ fontWeight: 600 }}
                  />
                  {!s.matched_client_name && (
                    <Chip
                      size="small"
                      color="info"
                      variant="outlined"
                      label={`Nombre: ${s.extracted_sender_name || "No detectado"}`}
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Stack>

                {s.processing_status === "failed" && (
                  <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
                    No pudimos leer este comprobante por completo. Puedes intentar de nuevo desde el menú.
                  </Typography>
                )}

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", flexDirection: { xs: "column", sm: "row" } }}>
                  {s.needs_order_completion ? (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={processingAction[s.id]}
                      onClick={() => onOpenOrderCompletion(s)}
                      fullWidth
                      sx={{
                        width: { xs: "100%", sm: "auto" },
                        backgroundColor: APP_PALETTE.surfaces.warningSoft,
                        color: APP_PALETTE.status.warning,
                        border: `1px solid ${APP_PALETTE.status.warning}33`,
                        boxShadow: "none",
                        fontWeight: 700,
                        "&:hover": {
                          backgroundColor: "#e7dfae",
                          boxShadow: "none",
                        },
                      }}
                    >
                      Confirmar y completar
                    </Button>
                  ) : (
                    <Button size="small" variant="contained" color="success" disabled={processingAction[s.id]} onClick={() => onSuggestionAction(s.id, "confirm") } fullWidth sx={{ width: { xs: "100%", sm: "auto" } }}>
                      Confirmar pago
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    disabled={processingAction[s.id]}
                    onClick={() => {
                      setRejectPayload(s.id);
                      setRejectConfirmOpen(true);
                    }}
                    fullWidth
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    Rechazar
                  </Button>
                </Box>

                {showReassign[s.id] && (
                  <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", flexDirection: { xs: "column", sm: "row" } }}>
                    <Autocomplete
                      options={reassignCandidates}
                      getOptionLabel={(o) => `${o.client?.full_name || "Cliente"} • Pedido #${o.id} • ${formatCurrencyBs(o.total)}`}
                      value={reassignOrder[s.id] || null}
                      onChange={(_, value) => setReassignOrder((prev) => ({ ...prev, [s.id]: value }))}
                      renderInput={(params) => (
                        <TextField {...params} size="small" placeholder="Elige el pedido correcto" />
                      )}
                      sx={{ width: { xs: "100%", sm: 360 }, minWidth: 0, flex: { xs: "1 1 100%", sm: "0 0 360px" } }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={processingAction[s.id] || !reassignOrder[s.id]?.id}
                      onClick={() => {
                        const selected = reassignOrder[s.id];
                        if (!selected?.id) return;
                        setConfirmPayload({ intakeId: s.id, order: selected });
                        setConfirmOpen(true);
                      }}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      Guardar cambio
                    </Button>
                  </Box>
                )}

                <Menu
                  anchorEl={advancedMenu.anchorEl}
                  open={advancedMenu.suggestionId === s.id}
                  onClose={closeAdvancedMenu}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem
                    onClick={async () => {
                      closeAdvancedMenu();
                      await onSuggestionAction(s.id, "reprocess");
                    }}
                    disabled={processingAction[s.id]}
                  >
                    Revisar de nuevo
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      closeAdvancedMenu();
                      setShowReassign((prev) => ({ ...prev, [s.id]: !prev[s.id] }));
                    }}
                  >
                    {showReassign[s.id] ? "Ocultar cambio" : "Cambiar pedido"}
                  </MenuItem>
                  <Divider />
                  <MenuItem disabled>Más opciones</MenuItem>
                </Menu>
              </Paper>
            );
          })}
        </Box>
      )}
      </Paper>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirmar cambio"
        description="¿Quieres guardar este comprobante en el pedido que elegiste?"
        confirmText="Sí, guardar"
        cancelText="Cancelar"
        onCancel={() => {
          setConfirmOpen(false);
          setConfirmPayload(null);
        }}
        onConfirm={async () => {
          setConfirmOpen(false);
          if (!confirmPayload) return;
          try {
            await onSuggestionAction(confirmPayload.intakeId, "reassign");
          } finally {
            setConfirmPayload(null);
          }
        }}
      >
        {confirmPayload?.order && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" fontWeight={700}>Pedido #{confirmPayload.order.id}</Typography>
            <Typography variant="caption">Cliente: {confirmPayload.order.client?.full_name || "-"}</Typography>
            <Typography variant="caption" display="block">Total: {formatCurrencyBs(confirmPayload.order.total)}</Typography>
          </Box>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={rejectConfirmOpen}
        title="Rechazar"
        description="¿Seguro que quieres rechazar este comprobante? Esta acción ayudará a sacarlo de la lista de revisión."
        confirmText="Sí, rechazar"
        confirmColor="error"
        cancelText="Cancelar"
        onCancel={() => {
          setRejectConfirmOpen(false);
          setRejectPayload(null);
        }}
        onConfirm={async () => {
          const intakeId = rejectPayload;
          setRejectConfirmOpen(false);
          setRejectPayload(null);
          if (!intakeId) return;
          await onSuggestionAction(intakeId, "reject");
        }}
      />
    </>
  );
}